import { Skeleton, SkeletonProps } from '@ui/lib/park/ui/skeleton';
import { TradePair } from '@ui/types/tradePair';
import Script from 'next/script';
import { memo, useEffect, useRef, useState } from 'react';
import { Box, BoxProps, styled } from 'styled-system/jsx';
import { ChartingLibraryWidgetOptions, IChartingLibraryWidget, IChartWidgetApi } from 'trading-view';

import { ChartContextContextProvider } from './chartContext';
import { defaultChartProps } from './constants';
import { load, save } from './utils/user-store';
// import { getVisibleWindowByResolution } from './utils/datafeed';

type PriceChartProps = Omit<BoxProps, 'id'> & {
  tradingPair: TradePair;
  id: string;
  chartProps?: Partial<ChartingLibraryWidgetOptions>;
  datafeed: ChartingLibraryWidgetOptions['datafeed'] & {
    setResetData(fn: () => void): void;
  };
  skeletonCss?: SkeletonProps['css'];
};
/**
 * The branded chart-loading visual: dark cover + the LeverUp mark rising
 * in a loop. Exported so gap states (chart slot not mounted yet / remount
 * windows) render the IDENTICAL visual — loading reads as one continuous
 * animation even across component remounts.
 */
export const ChartLoadingCover = (props: BoxProps) => (
  <Box
    bg="#0a0b0a"
    display="flex"
    alignItems="center"
    justifyContent="center"
    pointerEvents="none"
    {...props}
  >
    <styled.svg
      width="44px"
      height="47px"
      viewBox="0 0 100 107"
      fill="none"
      aria-label="Loading chart"
      overflow="visible"
      css={{
        '& path': {
          animation: 'leverup-bar 1.3s ease-in-out infinite',
        },
      }}
    >
      <path
        d="M31.5622 60.7949V92.4086C31.5622 100.467 25.0402 106.999 16.995 106.999H9.71215V91.315C9.71215 85.9424 5.36387 81.587 0 81.587L31.5622 60.7949Z"
        fill="#CCFF00"
        style={{ animationDelay: '0s' }}
      />
      <path
        d="M65.5527 29.1816V87.5453C65.5527 98.2898 56.8568 107 46.1298 107H43.7026V60.1329C43.7026 54.7602 39.3544 50.4049 33.9905 50.4049L65.5527 29.1816Z"
        fill="#CCFF00"
        style={{ animationDelay: '0.18s' }}
      />
      <path
        d="M99.5431 0V85.1144C99.5431 97.2015 89.7606 107 77.6931 107V30.6628C77.6931 25.2902 73.3448 20.9348 67.981 20.9348L99.5431 0Z"
        fill="#CCFF00"
        style={{ animationDelay: '0.36s' }}
      />
    </styled.svg>
  </Box>
);

/** charts that already painted once this page session — a remount (error
 *  boundary reset, provider re-key, chain switch) must NOT replay the
 *  loading cover; the user sees the logo animation exactly once */
const paintedOnceIds = new Set<string>();

export const PriceChart = memo(
  ({
    tradingPair,
    chartProps,
    datafeed,
    children,
    skeletonCss,
    ...p
  }: PriceChartProps) => {
    const symbol = tradingPair.baseSymbol;

    const [scriptLoadState, setScriptLoadState] = useState({
      chartingLibrary: false,
    });
    const [isTradingViewInit, setTradingViewInit] = useState(false); // true when charting lib scripted finish download
    const [isChartReady, setChartReady] = useState(false); // true when first load done
    const [isChartRefreshing, setChartRefreshing] = useState(true); // true when switching symbol caused chart refresh, not the same as `isChartReady`
    // true only after the first candle batch is painted — the skeleton keeps
    // covering the empty TradingView shell so a refresh reads as ONE load.
    // Seeded from module memory: remounts skip the cover entirely.
    const [isChartPainted, setChartPainted] = useState(() =>
      p.id ? paintedOnceIds.has(p.id) : false,
    );

    useEffect(() => {
      if (isChartPainted && p.id) paintedOnceIds.add(p.id);
    }, [isChartPainted, p.id]);

    const [chart, setChart] = useState<IChartWidgetApi>();
    const [widget, setWidget] = useState<IChartingLibraryWidget>();
    const widgetCreatedRef = useRef(false);
    const symbolRef = useRef(symbol);

    useEffect(() => {
      symbolRef.current = symbol;
    }, [symbol]);

    useEffect(() => {
      /**
       * script.onLoad only trigger if first time loading the script
       * the subsequential reference of this component will rely on whether window.TradingView is present
       */
      const isScriptFirstLoaded = scriptLoadState.chartingLibrary;
      setTradingViewInit(
        isScriptFirstLoaded ||
          (typeof window !== 'undefined' && !!window.TradingView),
      );
    }, [scriptLoadState]);

    useEffect(() => {
      // widgetCreatedRef guards SYNCHRONOUSLY: isChartReady flips only in
      // onChartReady (async), so a dep change during init (chartProps gets a
      // new identity on parent re-renders) used to create a SECOND widget in
      // the same container — the chart visibly loaded twice.
      if (!isTradingViewInit || !p.id || isChartReady) return;
      if (widgetCreatedRef.current) return;
      widgetCreatedRef.current = true;

      const savedData = load({ symbol });

      const widget = new window.TradingView.widget({
        ...defaultChartProps,
        ...chartProps,
        container: p.id,
        datafeed,
        symbol: symbol,
        saved_data: savedData,
      });

      setWidget(widget);

      widget.onChartReady(() => {
        widget.applyOverrides(defaultChartProps.overrides);
        const chart = widget.activeChart();
        setChart(chart);
        setChartReady(true);
        datafeed.setResetData(() => {
          widget.activeChart().resetData();
        });
        chart.onIntervalChanged().subscribe(
          null,
          () => {
            // interval change will make chart api throw error if data not loaded
            setChartRefreshing(true);
          },
          false,
        );
        // chart.on
        chart.onDataLoaded().subscribe(
          null,
          () => {
            // allow to use chart api function only if data loaded
            setChartRefreshing(false);
            setChartPainted(true);

            // not used yet for visible window
            // const res = chart.resolution();
            // const windowSec = getVisibleWindowByResolution(res);
            // const nowSec = Math.floor(Date.now() / 1000);
            // chart.setVisibleRange({ from: nowSec - windowSec, to: nowSec });
          },
          false,
        );
      });
    }, [symbol, isTradingViewInit, p.id, chartProps, datafeed, isChartReady]);

    // fallbacks: a bar-less market never fires onDataLoaded (2.5s after
    // ready), and a stuck init must never strand the cover (8s hard cap)
    useEffect(() => {
      if (!isChartReady || isChartPainted) return;
      const t = setTimeout(() => setChartPainted(true), 2500);

      return () => clearTimeout(t);
    }, [isChartReady, isChartPainted]);

    useEffect(() => {
      if (!isTradingViewInit || isChartPainted) return;
      const t = setTimeout(() => setChartPainted(true), 8000);

      return () => clearTimeout(t);
    }, [isTradingViewInit, isChartPainted]);

    useEffect(() => {
      if (!isChartReady || !widget) return;

      const interval = setInterval(() => {
        widget.save((state) => {
          save(state, { symbol: symbolRef.current });
        });
      }, 5 * 1000);

      return () => clearInterval(interval);
    }, [isChartReady, widget]);

    useEffect(
      function syncSymbolToChart() {
        if (!isTradingViewInit || !isChartReady || !chart) return;
        if (symbol !== chart.symbol()) {
          // save before switch
          const oldSymbol = chart.symbol();
          widget?.save((state) => {
            save(state, { symbol: oldSymbol });
          });

          // set symbol imperatively to avoid re-init the widget
          chart.setSymbol(symbol);
          setChartRefreshing(true);
        }
      },
      // dep list intended to be symbol only
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [symbol],
    );

    return (
      <ChartContextContextProvider
        chartApi={chart}
        isChartReady={!isChartRefreshing && isChartReady}
      >
        {/* Script is preloaded globally in _app.tsx for faster first paint */}
        {typeof window !== 'undefined' &&
          (window as any).TradingView &&
          !scriptLoadState.chartingLibrary && (
            <Script
              id="tv-ready-marker"
              strategy="afterInteractive"
              onLoad={() => {
                setScriptLoadState((p) => ({
                  ...p,
                  chartingLibrary: true,
                }));
              }}
            />
          )}

        {/* Skeleton only covers the script download — its not-loaded state
            hides ALL descendants (visibility:hidden), and TradingView cannot
            initialise inside a hidden container. The first-paint cover is our
            own overlay below, so the container stays live the whole time. */}
        <Skeleton
          isLoaded={!!isTradingViewInit}
          display="flex"
          flexDir="column"
          position="relative"
          css={skeletonCss}
        >
          {
            <Box
              background="#0a0b0a"
              height="100%"
              {...p}
            />
          }
          {/* dark cover until the first candle batch is painted — a refresh
              reads as ONE load instead of exposing the empty TV shell */}
          <ChartLoadingCover
            position="absolute"
            inset={0}
            zIndex={2}
            opacity={isChartPainted ? 0 : 1}
            transition="opacity 0.3s ease-in-out"
          />
          {children}
        </Skeleton>
      </ChartContextContextProvider>
    );
  },
);

PriceChart.displayName = 'PriceChart';
